import {
    ReactiveRepeatableMagramaSlot,
    ReactiveRepeatableMagramaSlotMapper,
} from './reactive-repeatable-magrama-slot-mapper';

export interface ReactiveRepeatableMagramaLoaderContext<TProduct extends Record<string, unknown>> {
    readonly rowIndex: number;
    readonly slot: ReactiveRepeatableMagramaSlot;
    readonly productId: string;
    readonly product: TProduct | null;
    readonly referencia: string;
}

export type ReactiveRepeatableMagramaLoader<
    TData,
    TProduct extends Record<string, unknown>,
> = (context: ReactiveRepeatableMagramaLoaderContext<TProduct>) => Promise<TData | null>;

export interface ReactiveRepeatableMagramaLoadRequest<TProduct extends Record<string, unknown>> {
    readonly rowIndex: number;
    readonly productId: string;
    readonly referencia: string;
    readonly product?: TProduct | null;
    readonly slot?: ReactiveRepeatableMagramaSlot;
}

export interface ReactiveRepeatableMagramaUnavailableResult {
    readonly status: 'unavailable';
    readonly rowIndex: number;
    readonly productId: string;
    readonly referencia: string;
}

export interface ReactiveRepeatableMagramaStaleResult {
    readonly status: 'stale';
    readonly rowIndex: number;
    readonly productId: string;
    readonly referencia: string;
}

export interface ReactiveRepeatableMagramaLoadedResult<TData> {
    readonly status: 'loaded';
    readonly rowIndex: number;
    readonly productId: string;
    readonly referencia: string;
    readonly data: TData | null;
}

export type ReactiveRepeatableMagramaLoadResult<TData> =
    ReactiveRepeatableMagramaUnavailableResult |
    ReactiveRepeatableMagramaStaleResult |
    ReactiveRepeatableMagramaLoadedResult<TData>;

/**
 * Owns repeatable MAGRAMA loader identity, request de-duplication and stale-row protection.
 */
export class ReactiveRepeatableMagramaDataBoundary<
    TData,
    TProduct extends Record<string, unknown>,
> {
    private loader: ReactiveRepeatableMagramaLoader<TData, TProduct> | null = null;
    private readonly loadPromises = new Map<string, Promise<TData | null>>();
    private readonly rowLoadKeys = new Map<number, string>();

    public setLoader(loader: ReactiveRepeatableMagramaLoader<TData, TProduct>): void {
        if (this.loader !== loader) {
            this.clear();
        }

        this.loader = loader;
    }

    public clear(): void {
        this.loadPromises.clear();
        this.rowLoadKeys.clear();
    }

    public clearRow(rowIndex: number): void {
        this.rowLoadKeys.delete(ReactiveRepeatableMagramaSlotMapper.normalizeRowIndex(rowIndex));
    }

    public getCachedRequestCount(): number {
        return this.loadPromises.size;
    }

    public async load(request: ReactiveRepeatableMagramaLoadRequest<TProduct>): Promise<ReactiveRepeatableMagramaLoadResult<TData>> {
        const rowIndex = ReactiveRepeatableMagramaSlotMapper.normalizeRowIndex(request.rowIndex);
        const productId = String(request.productId ?? '').trim();
        const referencia = String(request.referencia ?? '').trim();

        if (this.loader === null || productId === '' || referencia === '') {
            this.clearRow(rowIndex);

            return { status: 'unavailable', rowIndex, productId, referencia };
        }

        const slot = request.slot ?? ReactiveRepeatableMagramaSlotMapper.resolveThreeSlot(rowIndex);
        const loadKey = `${productId}:${referencia}`;
        const rowLoadKey = `${rowIndex}:${loadKey}`;
        const promise = this.loadPromises.get(loadKey)
            ?? this.loader({
                rowIndex,
                slot,
                productId,
                product: request.product ?? null,
                referencia,
            });

        this.loadPromises.set(loadKey, promise);
        this.rowLoadKeys.set(rowIndex, rowLoadKey);
        const data = await promise;

        if (this.rowLoadKeys.get(rowIndex) !== rowLoadKey) {
            return { status: 'stale', rowIndex, productId, referencia };
        }

        return { status: 'loaded', rowIndex, productId, referencia, data };
    }
}
